// src/pages/Team/TeamPersonModalForm.tsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Person, EventDto } from "../Admin/ID/types";
import "../../Pages/Admin/Event/AdminEvent.css";

interface Country {
    code: string;
}

interface FunctionsByEvent {
    eventID: number;
    functions: string[];
}

interface Props {
    initialPerson?: Person | null;
    onSave: (person: Person & { functionsByEvent?: FunctionsByEvent[] }) => void;
    onCancel: () => void;
    allEvents: EventDto[];
    allFunctions: { id: number; name: string }[];
    currentUserId: number;
    currentEventID: number;
}

interface PersonWithFunctionsByEvent extends Person {
    functionsByEvent?: FunctionsByEvent[];
}

const mapInitialFunctionsByEvent = (
    person?: Person | null,
    currentEventID?: number
): FunctionsByEvent[] => {
    if (!person || !currentEventID) return [];

    const existingFunctionsByEvent = (person as any)?.functionsByEvent;
    if (existingFunctionsByEvent?.length) return existingFunctionsByEvent;

    if (person.functions?.length) {
        return [
            {
                eventID: currentEventID,
                functions: [person.functions[0]],
            },
        ];
    }

    return [];
};

const TeamPersonModalForm: React.FC<Props> = ({
    initialPerson,
    onSave,
    onCancel,
    allEvents,
    allFunctions,
    currentUserId,
    currentEventID,
}) => {
    const isEdit = !!initialPerson?.personId;

    const [countries, setCountries] = useState<Country[]>([]);
    const [busy, setBusy] = useState(false);

    const [formData, setFormData] = useState<PersonWithFunctionsByEvent>(() => ({
        personId: initialPerson?.personId ?? 0,
        userId: initialPerson?.userId ?? currentUserId,
        firstName: initialPerson?.firstName ?? "",
        lastName: initialPerson?.lastName ?? "",
        dateOfBirth: initialPerson?.dateOfBirth,
        country: initialPerson?.country ?? "",
        title: initialPerson?.title ?? "",
        company: initialPerson?.company ?? "",
        events: initialPerson?.events ?? [],
        functions: initialPerson?.functions ?? [],
        functionsByEvent: mapInitialFunctionsByEvent(initialPerson, currentEventID),
        personImage: initialPerson?.personImage ?? "",
        created: initialPerson?.created ?? new Date().toISOString(),
    }));

    const [previewImage, setPreviewImage] = useState<string | undefined>(
        initialPerson?.personImage
    );

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/api/countries`)
            .then(res => setCountries(res.data))
            .catch(err => console.error("Failed to load countries", err));
    }, []);

    useEffect(() => {
        setFormData({
            personId: initialPerson?.personId ?? 0,
            userId: initialPerson?.userId ?? currentUserId,
            firstName: initialPerson?.firstName ?? "",
            lastName: initialPerson?.lastName ?? "",
            dateOfBirth: initialPerson?.dateOfBirth,
            country: initialPerson?.country ?? "",
            title: initialPerson?.title ?? "",
            company: initialPerson?.company ?? "",
            events: initialPerson?.events ?? [],
            functions: initialPerson?.functions ?? [],
            functionsByEvent: mapInitialFunctionsByEvent(initialPerson, currentEventID),
            personImage: initialPerson?.personImage ?? "",
            created: initialPerson?.created ?? new Date().toISOString(),
        });
        setPreviewImage(initialPerson?.personImage);
    }, [initialPerson, currentUserId, currentEventID]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            setPreviewImage(result);
            setFormData(prev => ({ ...prev, personImage: result }));
        };
        reader.readAsDataURL(file);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const removeTag = (field: "events" | "functions", idx: number) => {
        setFormData(prev => ({
            ...prev,
            [field]: (prev[field] as any[]).filter((_, i) => i !== idx),
        }));
    };

    const addEvent = (ev: EventDto) => {
        setFormData(prev => {
            if (prev.events.some(e => e.eventID === ev.eventID)) return prev;
            return { ...prev, events: [...prev.events, ev] };
        });
    };

    const addFunctionToEvent = (fn: string) => {
        setFormData(prev => {
            const existing = prev.functionsByEvent ?? [];
            const eventIndex = existing.findIndex(fe => fe.eventID === currentEventID);
            if (eventIndex >= 0) {
                const updated = existing.map((item, index) =>
                    index === eventIndex
                        ? { ...item, functions: [fn] }
                        : item
                );

                return { ...prev, functionsByEvent: updated };
            }

            return {
                ...prev,
                functionsByEvent: [
                    ...existing,
                    { eventID: currentEventID!, functions: [fn] }
                ],
            };
        });
    };

    const removeEvent = (eventID: number) => {
        setFormData(prev => ({
            ...prev,
            events: prev.events.filter(e => e.eventID !== eventID),
            functionsByEvent: (prev.functionsByEvent ?? []).filter(
                fe => fe.eventID !== eventID
            ),
        }));
    };

    const removeFunctionFromEvent = () => {
        setFormData(prev => {
            const existing = prev.functionsByEvent ?? [];
            const updated = existing.filter(fe => fe.eventID !== currentEventID);
            return { ...prev, functionsByEvent: updated };
        });
    };

    const currentEventFunction =
        formData.functionsByEvent?.find(fe => fe.eventID === currentEventID)?.functions?.[0] ?? "";

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Determine if profile picture is required
        const personBilledeRequired = allEvents
            .find(ev => ev.eventID === currentEventID)
            ?.eventSettings?.find(s => s.settingKey === "PersonBillede")?.settingValue === "1";

        // Validation
        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            alert("Firstname and Lastname are required.");
            return;
        }

        if (!formData.dateOfBirth) {
            alert("Date of Birth is required.");
            return;
        }

        if (!formData.events?.some(e => e.eventID === currentEventID)) {
            alert("Event is required.");
            return;
        }

        const currentFn = formData.functionsByEvent?.find(fe => fe.eventID === currentEventID)?.functions?.[0];
        if (!currentFn) {
            alert("Function is required.");
            return;
        }

        if (personBilledeRequired && !formData.personImage) {
            alert("Profile picture is required for this event.");
            return;
        }

        setBusy(true);

        // Flatten functionsByEvent into functions
        const personToSave = {
            ...formData,
            functions: [
                ...new Set(
                    formData.functionsByEvent?.flatMap(fe => fe.functions) ?? []
                ),
            ],
        };

        onSave(personToSave);
        setBusy(false);
    };

    return (
        <div className="modal-overlay" onMouseDown={onCancel}>
            <div className="modal-dialog" onMouseDown={e => e.stopPropagation()}>
                <div className="eventform-card">
                    <div className="eventform-header">
                        <h2 className="eventform-title">{isEdit ? "Edit Person" : "Add Person For Team"}</h2>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="eventform-grid">
                            <div className="field">
                                <label>Firstname</label>
                                <input name="firstName" value={formData.firstName} onChange={handleChange} required />
                            </div>
                            <div className="field">
                                <label>LastName</label>
                                <input name="lastName" value={formData.lastName} onChange={handleChange} required />
                            </div>
                            <div className="field">
                                <label>Date of Birth</label>
                                <input
                                    type="date"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth ? formData.dateOfBirth.split("T")[0] : ""}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="field">
                                <label>Country</label>
                                <select name="country" value={formData.country || ""} onChange={handleChange}>
                                    <option value="">Select Country</option>
                                    {countries.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                                </select>
                            </div>
                            <div className="field">
                                <label>Title</label>
                                <input
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="field">
                                <label>Company</label>
                                <input
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                />
                            </div>
                            {/* EVENTS - only current event */}
                            <div className="field" style={{ gridColumn: "1 / -1" }}>
                                <label>Event</label>
                                <div className="chip-row">
                                    {formData.events
                                        .filter(ev => ev.eventID === currentEventID)
                                        .map((ev, idx) => (
                                            <span className="chip" key={ev.eventID}>
                                                <span className="chip-text">{ev.eventName}</span>
                                                <button
                                                    type="button"
                                                    className="chip-x"
                                                    onClick={() => removeEvent(ev.eventID)}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                </div>
                                <select
                                    className="styled-dropdown"
                                    onChange={e => {
                                        const eventID = Number(e.target.value);
                                        if (!eventID) return;

                                        const event = allEvents.find(ev => ev.eventID === eventID);
                                        if (event) addEvent(event);
                                    }}
                                >
                                    <option value="">Add Event</option>
                                    {allEvents.map(ev => (
                                        <option key={ev.eventID} value={ev.eventID}>
                                            {ev.eventName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* FUNCTIONS - per current event */}
                            <div className="field" style={{ gridColumn: "1 / -1" }}>
                                <label>Function</label>
                                <div className="chip-row">
                                    {currentEventFunction && (
                                        <span className="chip">
                                            <span className="chip-text">{currentEventFunction}</span>
                                            <button
                                                type="button"
                                                className="chip-x"
                                                onClick={removeFunctionFromEvent}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    )}
                                </div>

                                <select
                                    className="styled-dropdown"
                                    value={currentEventFunction || ""}
                                    onChange={e => {
                                        const value = e.target.value;
                                        addFunctionToEvent(value);
                                    }}
                                >
                                    <option value="">Select Function</option>
                                    {allFunctions.map(fn => (
                                        <option key={fn.id} value={fn.name}>
                                            {fn.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="field">
                                <label>Profile Picture</label>
                                {previewImage && <img src={previewImage} alt="Preview" style={{ width: 120, height: 120, borderRadius: "50%", objectFit: "cover" }} />}
                                <input type="file" accept="image/*" onChange={handleImageChange} />
                            </div>
                        </div>

                        <div className="eventform-actions">
                            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>Close</button>
                            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Saving..." : "Save"}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TeamPersonModalForm;